import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQuizByShareCode, submitQuizAnswers, type Quiz as BaseQuiz } from "@/lib/quiz-api";
import QuizQuestion from "@/components/quiz-question";
import QuizResultCard from "@/components/quiz-result-card";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import ConfettiCanvas from "@/components/confetti-canvas";
import html2canvas from "html2canvas";
import { Download, Send, Heart, Gift, Sparkles, Flower } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { QuizTheme } from "@/lib/theme-presets";
import { fadeIn, slideInFromBottom, slideInFromLeft, slideInFromRight, staggerContainer } from "@/lib/animations";

// Extend the Quiz type to include theme
interface Quiz extends BaseQuiz {
  theme: QuizTheme;
}

export default function TakeQuiz({ params }: { params: { shareCode: string } }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [playerName, setPlayerName] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [nameSubmitted, setNameSubmitted] = useState(false);
  const resultCardRef = useRef<HTMLDivElement>(null);
  const [direction, setDirection] = useState(1);
  const [errorState, setError] = useState<Error | null>(null);
  const [isLogoHovered, setIsLogoHovered] = useState(false);

  const { data: quiz, isLoading, error: queryError } = useQuery<Quiz>({
    queryKey: [`/api/quiz/share/${params.shareCode}`],
    queryFn: async () => {
      const response = await fetch(`/api/quiz/share/${params.shareCode}`);
      if (!response.ok) {
        const errorData = await response.json();
        // Create a custom error that includes the response error message and error type
        const customError = new Error(errorData.error || 'Failed to fetch quiz');
        // Store the additional error information as a property of the error object
        (customError as any).errorData = errorData;
        throw customError;
      }
      return response.json();
    },
    enabled: !!params.shareCode,
  });

  const submitScore = useMutation({
    mutationFn: () => submitQuizAnswers(params.shareCode, {
      playerName: playerName.trim(),
      answers,
    }),
    onSuccess: (data) => {
      setNameSubmitted(true);
      toast({
        title: "Success!",
        description: "Your score has been submitted.",
      });
    },
    onError: (error: any) => {
      if (error.message?.includes("Response limit reached")) {
        toast({
          title: "Response Limit Reached",
          description: "This quiz has reached its maximum number of responses for the free plan. The creator can upgrade to premium for unlimited responses.",
          variant: "default",
          className: "bg-pink-50 border-pink-200",
        });
        // Redirect to response limit page after showing toast
        setTimeout(() => {
          setQuizComplete(false);
          setError(new Error("Response limit reached"));
        }, 1500);
      } else {
        toast({
          title: "Error",
          description: "Failed to submit score. Please try again.",
          variant: "destructive",
        });
      }
    }
  });

  const handleNameSubmit = () => {
    if (!playerName.trim()) {
      toast({
        title: "Error",
        description: "Please enter your name",
        variant: "destructive",
      });
      return;
    }
    submitScore.mutate();
  };

  const handleAnswer = (answer: string) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answer;
    setAnswers(newAnswers);

    if (currentQuestion < quiz!.questions.length - 1) {
      setDirection(1);
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setQuizComplete(true);
      setShowConfetti(true);
    }
  };

  const handleDownloadResult = async () => {
    if (!resultCardRef.current) return;

    try {
      const canvas = await html2canvas(resultCardRef.current, {
        backgroundColor: null,
        scale: 2,
      });

      const image = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = image;
      link.download = `quiz-result-${quiz?.shareCode || "result"}.png`;
      link.click();

      toast({
        title: "Success!",
        description: "Your quiz result has been downloaded.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate result image. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Get theme-appropriate icon
  const QuizIcon = () => {
    if (!quiz) return null;
    
    const iconClass = "h-14 w-14 mb-4";
    const iconStyle = { color: quiz.theme.primaryColor };
    
    switch(quiz.theme.welcomeIcon) {
      case 'hearts': 
        return <Heart className={iconClass} style={iconStyle} />;
      case 'rings': 
        return <Sparkles className={iconClass} style={iconStyle} />;
      case 'doves': 
        return <Flower className={iconClass} style={iconStyle} />;
      default: 
        return <Heart className={iconClass} style={iconStyle} />;
    }
  };

  // Add some debugging for the error
  useEffect(() => {
    if (queryError) {
      console.log("Query error:", queryError);
      console.log("Error message:", queryError.message);
      // Try to extract the error data if available
      const errorData = (queryError as any).errorData;
      if (errorData) {
        console.log("Error data:", errorData);
      }
    }
  }, [queryError]);

  // Check for response limit error based on the structure we now know from debugging
  if (queryError && (queryError.message === "Response limit reached" || 
      ((queryError as any).errorData && (queryError as any).errorData.error === "Response limit reached"))) {
    return (
      <div className="min-h-screen py-12 bg-gradient-to-b from-pink-50 to-white">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="p-8 text-center border border-pink-100 shadow-xl">
            <div className="mb-6 flex justify-center">
              <div className="rounded-full bg-pink-100 p-4 inline-block">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-pink-500">
                  <path d="M2 18v3c0 .6.4 1 1 1h4v-3h3v-3h2l1.4-1.4a6.5 6.5 0 1 0-4-4Z"></path>
                  <circle cx="17" cy="7" r="1"></circle>
                </svg>
              </div>
            </div>
            <h2 className="text-2xl font-serif mb-4 text-gray-900">Response Limit Reached</h2>
            <p className="text-gray-600 mb-2">
              This quiz has reached its maximum number of responses for the free plan.
            </p>
            <p className="text-gray-600 mb-6">
              The quiz creator can upgrade to premium for unlimited responses and other benefits!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="secondary" onClick={() => setLocation("/")}>
                Return Home
              </Button>
              <Button 
                className="bg-pink-600 hover:bg-pink-700"
                onClick={() => window.open("/pricing", "_blank")}
              >
                View Premium Benefits
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }
  
  // Loading state
  if (isLoading || !quiz) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-pink-50 to-white p-4">
        <div className="text-center">
          <div className="inline-block relative mb-6">
            <div className="animate-ping absolute inline-flex h-10 w-10 rounded-full bg-pink-400 opacity-50"></div>
            <div className="relative inline-flex rounded-full h-10 w-10 bg-pink-500 items-center justify-center">
              <Heart className="h-5 w-5 text-white" />
            </div>
          </div>
          <h2 className="text-xl font-medium text-gray-700 mb-2">Loading Quiz</h2>
          <p className="text-sm text-gray-500">Preparing your wedding quiz experience...</p>
        </div>
      </div>
    );
  }

  // Welcome screen
  if (!quizStarted) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ 
          background: `linear-gradient(135deg, ${quiz.theme.secondaryColor}30, white, ${quiz.theme.secondaryColor}20)`,
        }}
      >
        {/* Decorative elements */}
        <div className="fixed top-8 left-8 opacity-5 pointer-events-none hidden md:block">
          <svg width="120" height="120" viewBox="0 0 24 24" fill={quiz.theme.primaryColor} xmlns="http://www.w3.org/2000/svg">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </div>
        <div className="fixed bottom-8 right-8 opacity-5 pointer-events-none hidden md:block">
          <svg width="150" height="150" viewBox="0 0 24 24" fill={quiz.theme.primaryColor} xmlns="http://www.w3.org/2000/svg">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </div>
        
        <motion.div
          className="container mx-auto px-4 max-w-lg z-10"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <Card className="p-8 md:p-10 text-center border-2 shadow-xl" 
            style={{ 
              borderColor: `${quiz.theme.primaryColor}30`,
              background: `linear-gradient(135deg, white, ${quiz.theme.secondaryColor}20)`
            }}
          >
            {/* Quiz icon */}
            <motion.div 
              className="flex justify-center mb-6"
              variants={slideInFromBottom}
              whileHover={{ 
                scale: 1.1,
                rotate: isLogoHovered ? [0, -10, 10, -10, 10, 0] : 0,
                transition: { 
                  duration: 0.5,
                  type: "spring",
                  bounce: 0.5
                }
              }}
              onHoverStart={() => setIsLogoHovered(true)}
              onHoverEnd={() => setIsLogoHovered(false)}
            >
              {QuizIcon()}
            </motion.div>
            
            {/* Quiz title */}
            <motion.h1 
              className="text-3xl sm:text-4xl font-serif mb-6 font-medium"
              style={{ color: quiz.theme.primaryColor }}
              variants={slideInFromBottom}
            >
              {quiz.title}
            </motion.h1>
            
            {/* Quiz welcome message */}
            {quiz.welcomeMessage && (
              <motion.div 
                className="mb-8"
                variants={slideInFromBottom}
              >
                <p className="text-gray-600 whitespace-pre-wrap leading-relaxed bg-white bg-opacity-50 p-4 rounded-lg shadow-sm">
                  {quiz.welcomeMessage}
                </p>
              </motion.div>
            )}
            
            {/* Decorative element */}
            <motion.div 
              className="my-6" 
              variants={slideInFromBottom}
            >
              <div className="flex items-center justify-center">
                <div className="h-px bg-gray-200 w-16"></div>
                <div className="mx-4">
                  <svg width="24" height="24" viewBox="0 0 24 24" className="text-gray-300">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" 
                      fill={quiz.theme.primaryColor} />
                  </svg>
                </div>
                <div className="h-px bg-gray-200 w-16"></div>
              </div>
            </motion.div>
            
            {/* Start button */}
            <motion.div
              variants={slideInFromBottom}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                onClick={() => setQuizStarted(true)}
                className={cn(
                  "transition-all duration-300 px-8 py-6 text-lg shadow-md",
                  quiz.theme.buttonStyle === "pill" && "rounded-full",
                  quiz.theme.buttonStyle === "square" && "rounded-none"
                )}
                style={{
                  backgroundColor: quiz.theme.primaryColor,
                  borderColor: quiz.theme.primaryColor,
                }}
              >
                <Gift className="mr-2 h-5 w-5" />
                Start Quiz
              </Button>
            </motion.div>
            
            {/* Small hint text */}
            <motion.p 
              className="text-xs text-gray-400 mt-6"
              variants={slideInFromBottom}
            >
              This quiz contains {quiz.questions.length} questions
            </motion.p>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Main quiz and results
  return (
    <div 
      className="min-h-screen py-8 sm:py-12"
      style={{ 
        background: `linear-gradient(135deg, ${quiz.theme.secondaryColor}30, white, ${quiz.theme.secondaryColor}20)`,
      }}
    >
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Quiz title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 text-center"
        >
          <div 
            className="inline-flex items-center justify-center bg-white bg-opacity-70 px-6 py-2 rounded-full shadow-sm"
            style={{ borderBottom: `3px solid ${quiz.theme.primaryColor}30` }}
          >
            <h1 
              className="text-2xl sm:text-3xl font-serif"
              style={{ color: quiz.theme.primaryColor }}
            >
              {quiz.title}
            </h1>
          </div>
        </motion.div>

        {/* Question or name entry or results */}
        {!quizComplete ? (
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              key={currentQuestion}
              initial={{ x: 100 * direction, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -100 * direction, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="flex items-center justify-center"
            >
              <QuizQuestion
                question={quiz.questions[currentQuestion]}
                onAnswer={handleAnswer}
                currentNumber={currentQuestion + 1}
                total={quiz.questions.length}
                quiz={quiz}
              />
            </motion.div>
          </AnimatePresence>
        ) : !nameSubmitted ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="flex items-center justify-center min-h-[60vh]"
          >
            <Card 
              className="p-8 md:p-12 shadow-lg border-2 w-full max-w-xl mx-auto" 
              style={{
                borderColor: `${quiz.theme.primaryColor}30`,
                background: `linear-gradient(135deg, white, ${quiz.theme.secondaryColor}20)`
              }}
            >
              <div className="space-y-6">
                <div className="text-center">
                  <div className="flex justify-center mb-2">
                    <QuizIcon />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-medium mb-2"
                    style={{ color: quiz.theme.primaryColor }}>
                    Great Job!
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Please enter your name to see your results
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="text"
                    placeholder="Enter your name"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    className="flex-1 text-lg p-6"
                    style={{
                      borderColor: `${quiz.theme.primaryColor}50`,
                      borderWidth: "2px",
                    }}
                  />
                  <Button 
                    onClick={handleNameSubmit}
                    className={cn(
                      "gap-2 px-6 py-6 text-base sm:text-lg font-medium shadow-md",
                      quiz.theme.buttonStyle === "pill" && "rounded-full",
                      quiz.theme.buttonStyle === "square" && "rounded-none"
                    )}
                    style={{
                      backgroundColor: quiz.theme.primaryColor,
                      borderColor: quiz.theme.primaryColor,
                    }}
                  >
                    <Send className="h-5 w-5" />
                    Submit
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        ) : (
          <div className="pb-12 flex flex-col items-center justify-center min-h-[60vh]">
            <div ref={resultCardRef} className="w-full max-w-xl mx-auto">
              <QuizResultCard
                quiz={quiz}
                playerName={playerName}
                score={answers.reduce((total, answer, index) => {
                  const question = quiz!.questions[index];
                  return total + (answer === question.correctAnswer ? 1 : 0);
                }, 0)}
                totalQuestions={quiz.questions.length}
              />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="mt-8 w-full max-w-xl mx-auto"
            >
              <Card 
                className="p-8 shadow-lg border border-gray-100" 
                style={{
                  background: `linear-gradient(135deg, white, ${quiz.theme.secondaryColor}20)`
                }}
              >
                <div className="space-y-4">
                  <p className="text-center text-gray-600 mb-4">
                    Share your results with friends and family!
                  </p>
                  <Button
                    onClick={handleDownloadResult}
                    variant="outline"
                    className={cn(
                      "w-full gap-2 py-5 text-base shadow-sm",
                      quiz.theme.buttonStyle === "pill" && "rounded-full",
                      quiz.theme.buttonStyle === "square" && "rounded-none"
                    )}
                    style={{
                      borderColor: quiz.theme.primaryColor,
                      borderWidth: "2px",
                      color: quiz.theme.primaryColor,
                    }}
                  >
                    <Download className="h-5 w-5" />
                    Download Result
                  </Button>
                </div>
              </Card>
            </motion.div>
          </div>
        )}

        {showConfetti && <ConfettiCanvas />}
      </div>
    </div>
  );
}